import React, { createContext, useContext, useRef, useState } from "react"
import {
  DeleteRedemptionPage,
  GetRedeemCodeInfo,
  GetRedeemInfoFromServerSession,
  GetRedemptionPage,
  ScrambledRedeemCode,
} from "../Code/Data"

const RedemptionDataContext = createContext()

export const RedemptionDataProvider = ({ children }) => {
  const [redeemCodeInfo, setRedeemCodeInfo] = useState()
  const [redemptionPageInfo, setRedemptionPageInfo] = useState()
  // Tracks the latest redeem-code lookup so out-of-order responses from
  // earlier (now-stale) requests can be discarded instead of clobbering the
  // current code's result. This is what caused the "I typed a valid code but
  // Continue stays disabled" bug — a slow response for a previously-typed
  // invalid code arrived after the valid-code response and overwrote it.
  const codeLookupSeqRef = useRef(0)
  const [redemptionData, setRedemptionData] = useState({
    Charities: [],
    ShareInfo: {
      fName: "",
      lName: "",
      email: "",
    },
  })
  const [redemptionPageData, setRedemptionPageData] = useState({
    HeaderImageURL: "",
    hasLogo: true,
    MainColor: "#aaa",
    AccentColor: "#555",
    Charities: [],
  })

  async function getRedeemCodeInfo(redeemCode, userId) {
    if (!redeemCode)
      return { Succeeded: false, ErrorMessage: "Code is missing" }
    const mySeq = ++codeLookupSeqRef.current
    const redeemCodeInfo = await GetRedeemCodeInfo(redeemCode)
    // A newer lookup was started after this one — drop this stale response.
    if (mySeq !== codeLookupSeqRef.current) return null
    const { Succeeded, RedeemInfo, ErrorMessage } = redeemCodeInfo || {}
    const rci = { ...RedeemInfo, Succeeded, ErrorMessage }
    setRedeemCodeInfo(rci)
    //The userId is passed from useParams... meaning from the URL.
    if (userId) {
      getRedemptionPageInfo(userId)
    }
    else {
      if (Succeeded) {
        setRedemptionPageInfo(RedeemInfo?.UserRedemptionPageInfo)
      }
      else {
        setRedemptionPageInfo()
      }
    }

    return rci
  }

  async function getScrambledRedeemCode(scrambledCode, userId) {
    const mySeq = ++codeLookupSeqRef.current
    const scrambledRedeemCodeInfo = await ScrambledRedeemCode(scrambledCode)
    if (mySeq !== codeLookupSeqRef.current) return null
    const { Succeeded, RedeemInfo, ErrorMessage } =
      scrambledRedeemCodeInfo || {}
    const rci = { ...RedeemInfo, Succeeded, ErrorMessage }
    setRedeemCodeInfo(rci)
    if (parseInt(userId)) {
      getRedemptionPageInfo(parseInt(userId))
    }
    else {
      if (Succeeded) {
        setRedemptionPageInfo(RedeemInfo?.UserRedemptionPageInfo)
      }
      else {
        setRedemptionPageInfo()
      }
    }
    return rci
  }

  async function getRedeemInfoFromServerSession() {
    const redeemCodeInfo = await GetRedeemInfoFromServerSession()
    const { Succeeded, RedeemInfo, ErrorMessage } = redeemCodeInfo || {}
    const rci = { ...RedeemInfo, Succeeded, ErrorMessage }
    if (RedeemInfo && !RedeemInfo.Charities) RedeemInfo.Charities = []
    setRedeemCodeInfo(rci)
    if (Succeeded) {
      setRedemptionPageInfo(RedeemInfo?.UserRedemptionPageInfo)
    } else {
      setRedemptionPageInfo()
    }
    return rci
  }

  async function deleteRedemptionPage(userId) {
    if (!!userId) {
      const { Succeeded } = await DeleteRedemptionPage(userId)
      return Succeeded
    }
  }

  async function getRedemptionPageInfo(userId) {
    if (!redemptionPageInfo || redemptionPageInfo.UserId !== userId) {
      const { UserRedemptionPageInfo } = await GetRedemptionPage(userId)
      setRedemptionPageInfo(UserRedemptionPageInfo || undefined)
    }
    return redemptionPageInfo
  }

  function redirectOldCustomRedemptionPage(redeemCodeInfo) {
    if (!!redeemCodeInfo && !!redeemCodeInfo.RedemptionPageURL) {
      if (redeemCodeInfo.HasCustomRedemptionPage) {
        window.location = redeemCodeInfo.RedemptionPageURL
      } else if (
        redeemCodeInfo.RedemptionPageURL.toLowerCase().indexOf(
          "/pages/customredemptionpages/"
        ) > -1
      ) {
        window.location = redeemCodeInfo.RedemptionPageURL
      }
    }
  }

  return (
    <RedemptionDataContext.Provider
      value={{
        getRedeemCodeInfo,
        getScrambledRedeemCode,
        getRedeemInfoFromServerSession,
        getRedemptionPageInfo,
        redemptionData,
        setRedemptionData,
        redemptionPageData,
        setRedemptionPageData,
        setRedeemCodeInfo,
        redeemCodeInfo,
        redemptionPageInfo,
        deleteRedemptionPage,
        redirectOldCustomRedemptionPage,
      }}>
      {children}
    </RedemptionDataContext.Provider>
  )
}

export const useRedemptionData = () => useContext(RedemptionDataContext)
